import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useQueryClient } from 'react-query'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { SnackbarContext } from '../../../providers/SnackbarContext'
import { formatDate } from '../../../utils/formatting'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { WeatherGroup } from '../../../models/WeatherGroup.model'

import UserService from '../../../services/UserService/UserService'
import { WeatherGroupService } from '../../../services/WeatherGroupService/WeatherGroupService'

import WeatherRecordSearch from '../../WeatherRecords/WeatherRecordSearch/WeatherRecordSearch.route'

import startCase from 'lodash/startCase'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import Tabs from '../../../components/Tabs/Tabs.component'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import WeatherGroupForm from '../shared/WeatherGroupForm.component'
import { AppStateContext } from '../../../providers/AppStateContext'
import { formatWeatherSourceLabel } from '../../../models/WeatherSources.model'
import { isCoordinateWeatherSource } from '../../../utils/weatherSourceValidation'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { WeatherGroupSectionIds, weatherGroupSections } from '../shared/WeatherGroupSections'

const WeatherGroupDetail = (): JSX.Element => {
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editWeatherGroup])
  const hasWeatherRecordPermissions = UserService.hasPermissions(user, [AppPermissions.viewWeatherRecord])

  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const weatherGroupService = useMemo(() => new WeatherGroupService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Weather Group Detail`)
  }, [id])

  const queryClient = useQueryClient()
  const { isLoading, data: weatherGroup } = useQuery(
    [
      'WeatherGroupService.get',
      {
        id: Number(id),
      },
      AppStateContext.getRegion(),
    ],
    () => weatherGroupService.get(Number(id)),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load weather group.')
        console.error(err)
      },
    },
  )

  const handleEdit = () => {
    closeEditor()
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (weatherGroup?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      weatherGroupService
        .delete(weatherGroup.id)
        .then(() => {
          SnackbarContext.show(`Weather Group deleted successfully. You will now be redirected.`)

          // after a delay of 1 second, redirect to the weatherGroup list page
          setTimeout(() => {
            navigate('/weather-groups')
            queryClient.removeQueries([
              'WeatherGroupService.get',
              {
                id: Number(id),
              },
            ])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Weather Group failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const tabs = useMemo(() => {
    const tabs = []
    if (hasWeatherRecordPermissions) {
      tabs.push({
        label: 'Weather Records',
        content: <WeatherRecordSearch nested weatherGroupId={weatherGroup?.id} hideBreadcrumbs hideHeader />,
      })
    }
    return tabs
  }, [weatherGroup, hasWeatherRecordPermissions])

  const sectionById = (sectionId: WeatherGroupSectionIds) =>
    weatherGroupSections.find((section) => section.id === sectionId) as SectionDefinition
  // only offer per-section edit shortcuts to users who can edit
  const editSectionHandler = (sectionId: WeatherGroupSectionIds) =>
    hasWritePermissions ? () => openEditor(sectionId) : undefined

  return (
    <>
      {weatherGroup && (
        <FormDialog<WeatherGroup>
          open={isEditing}
          entity={weatherGroup}
          entityLabel="Weather Group"
          onClose={closeEditor}
        >
          <WeatherGroupForm
            weatherGroup={weatherGroup}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this weather group?</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setIsDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && weatherGroup && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={[{ label: 'Weather Groups', href: '/weather-groups' }, { label: weatherGroup.name }]}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <DetailHeader
                  title={weatherGroup.name}
                  subtitle={`${weatherGroup.region} · ID ${weatherGroup.id}`}
                  meta={[
                    `Updated ${formatDate(weatherGroup.updatedAt)}`,
                    `Created ${formatDate(weatherGroup.createdAt)}`,
                  ]}
                />

                <Grid container spacing={2}>
                  {/* --- General ------------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(WeatherGroupSectionIds.general)}
                      onEdit={editSectionHandler(WeatherGroupSectionIds.general)}
                    >
                      <DetailFieldGrid>
                        <DetailField label="Name">{weatherGroup.name || '-'}</DetailField>
                        <DetailField label="Type">{weatherGroup.type ? startCase(weatherGroup.type) : '-'}</DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Weather Source ------------------------------------------ */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(WeatherGroupSectionIds.source)}
                      onEdit={editSectionHandler(WeatherGroupSectionIds.source)}
                    >
                      <DetailFieldGrid>
                        <DetailField label="Weather Source">
                          {weatherGroup.weatherSource ? formatWeatherSourceLabel(weatherGroup.weatherSource) : '-'}
                        </DetailField>
                        <DetailField label="Weather Source Id">
                          {!weatherGroup.weatherSourceId ? (
                            '-'
                          ) : isCoordinateWeatherSource(weatherGroup.weatherSource) ? (
                            <Link
                              component="a"
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                weatherGroup.weatherSourceId.replace(/\s/g, ''),
                              )}`}
                              target="_blank"
                            >
                              {weatherGroup.weatherSourceId}
                            </Link>
                          ) : (
                            weatherGroup.weatherSourceId
                          )}
                        </DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>
                </Grid>

                {tabs.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Tabs tabs={tabs} />
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default WeatherGroupDetail
